import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Sparkles, Share2, X, Copy, Check, ArrowRight, ChevronUp, Download } from "lucide-react";
import { toPng } from "html-to-image";
import { hasSeenMilestone, markMilestoneSeen } from "@/lib/milestones";

// ---------------------------------------------------------------------------
// CareerMilestoneBanner - full-width, breaking-news style celebration.
// Slides in for ~6s then auto-minimises into a dismissible pill in the
// bottom-right. The user can also click "Share Achievement" to open a card
// they can copy to clipboard.
// ---------------------------------------------------------------------------

const ACCENT_STYLES = {
  gold: {
    ring: "border-amber-400/40",
    surface: "from-amber-400/[0.10] via-amber-500/[0.05] to-zinc-950/70",
    label: "text-amber-300 border-amber-400/40 bg-amber-400/10",
    headline: "text-amber-200",
    glow: "shadow-[0_0_120px_-30px_rgba(251,191,36,0.55)]",
    delta: "text-amber-300 bg-amber-400/10 border-amber-400/30",
    ctaBtn: "bg-amber-400 text-zinc-950 hover:bg-amber-300",
    dot: "bg-amber-400",
  },
  green: {
    ring: "border-emerald-500/40",
    surface: "from-emerald-500/[0.08] via-emerald-500/[0.04] to-zinc-950/70",
    label: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    headline: "text-emerald-200",
    glow: "shadow-[0_0_120px_-30px_rgba(16,185,129,0.55)]",
    delta: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    ctaBtn: "bg-emerald-400 text-zinc-950 hover:bg-emerald-300",
    dot: "bg-emerald-400",
  },
  emerald: {
    ring: "border-emerald-500/30",
    surface: "from-emerald-500/[0.06] via-zinc-900/40 to-zinc-950/70",
    label: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    headline: "text-zinc-50",
    glow: "shadow-[0_0_100px_-40px_rgba(16,185,129,0.55)]",
    delta: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    ctaBtn: "bg-zinc-50 text-zinc-950 hover:bg-white",
    dot: "bg-emerald-400",
  },
};

// Very lightweight CSS-only confetti - 24 tiny squares with staggered fall.
function Confetti() {
  const pieces = useMemo(
    () => Array.from({ length: 22 }, (_, i) => ({
      id: i,
      left: Math.round((i / 22) * 100 + (Math.random() * 6 - 3)),
      delay: Math.random() * 0.8,
      duration: 1.6 + Math.random() * 1.2,
      hue: [45, 48, 155, 152][i % 4],
      size: 5 + Math.random() * 5,
      rotate: Math.round(Math.random() * 360),
    })),
    [],
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute top-0 block rounded-[2px]"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 0.4}px`,
            background: `hsl(${p.hue}, 90%, 60%)`,
            transform: `rotate(${p.rotate}deg)`,
            animation: `orMilestoneFall ${p.duration}s ease-in ${p.delay}s forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes orMilestoneFall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          15% { opacity: 1; }
          100% { transform: translateY(220px) rotate(540deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function ScoreCard({ card, accentTone }) {
  const styles = ACCENT_STYLES[accentTone] || ACCENT_STYLES.gold;
  const denom = card.denom;
  if (card.single) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3 min-w-[168px]">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{card.label}</div>
        <div className="mt-1 flex items-baseline gap-1.5">
          <span className={`text-3xl font-semibold tabular-nums ${styles.headline}`}>{card.after}</span>
          {denom ? <span className="text-sm text-zinc-500">/{denom}</span> : <span className="text-sm text-zinc-500">%</span>}
        </div>
      </div>
    );
  }
  const delta = card.delta ?? (typeof card.before === "number" ? card.after - card.before : 0);
  const deltaText = delta > 0
    ? `\u25B2 +${delta} percentage point${delta === 1 ? "" : "s"}`
    : delta < 0
      ? `\u25BC ${delta} percentage point${Math.abs(delta) === 1 ? "" : "s"}`
      : "No change.";
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3 min-w-[220px]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{card.label}</div>
      <div className="mt-1 flex items-baseline gap-2 text-sm">
        <span className="text-zinc-500 tabular-nums line-through decoration-zinc-700/70">{card.before}%</span>
        <ArrowRight className="h-3 w-3 text-zinc-600" />
        <span className={`text-2xl font-semibold tabular-nums ${styles.headline}`}>{card.after}%</span>
      </div>
      <div className={`mt-2 inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[11px] font-semibold ${styles.delta}`}>
        {deltaText}
      </div>
    </div>
  );
}

function ShareModal({ milestone, open, onClose }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState(null);
  const [pngReady, setPngReady] = useState(false);
  const [linkedInHint, setLinkedInHint] = useState(false);
  const cardRef = useRef(null);

  if (!open || !milestone) return null;
  const share = milestone.share || {};

  // Pre-written LinkedIn caption. Uses the milestone lines as bullets and
  // signs off with a subtle brand tag so the whole thing reads as authentic.
  const captionLines = [
    share.title || milestone.headline,
    ...(share.lines || []),
  ].filter(Boolean);
  const linkedInCaption = [
    (share.caption || `${captionLines[0]}. One more step closer to interview day.`),
    "",
    ...captionLines.slice(1).map((l) => `• ${l}`),
    "",
    "Preparing like it's real with Intuiterview.",
    "#InterviewPrep #CareerGrowth #Intuiterview",
  ].filter((l) => l !== undefined).join("\n");

  const shareText = linkedInCaption;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  const filenameSlug = (share.title || milestone.headline || "milestone")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40) || "milestone";

  const downloadPng = async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    setDownloadErr(null);
    try {
      // Render at 2x for crisp LinkedIn/Twitter uploads.
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#09090b",
        style: { transform: "none" },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `intuiterview-${filenameSlug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setPngReady(true);
    } catch (e) {
      setDownloadErr("Couldn't generate the image. Try copying the text instead.");
    } finally {
      setDownloading(false);
    }
  };

  const shareToLinkedIn = async () => {
    // LinkedIn's public share intent no longer honours pre-filled text for
    // image posts, so the smoothest UX is:
    //   1. Copy the caption to clipboard automatically.
    //   2. Open LinkedIn's composer in a new tab with our URL attached.
    //   3. Prompt the user to paste the caption + attach the downloaded PNG.
    try {
      await navigator.clipboard.writeText(linkedInCaption);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* noop, some browsers block without user gesture */ }
    setLinkedInHint(true);
    const shareUrl = "https://www.linkedin.com/sharing/share-offsite/?url=" +
      encodeURIComponent("https://intuiterview.com");
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  // A purpose-built card DOM - designed for PNG export at fixed dimensions
  // (1200x630 is LinkedIn/Twitter card standard). The user sees a scaled
  // preview inside the modal; the export renders at full resolution.
  const scoreCards = milestone.scoreCards || [];
  const hasBrand = true;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="milestone-share-modal"
    >
      <div
        className="w-full max-w-[720px] rounded-2xl border border-amber-400/30 bg-zinc-950 shadow-[0_20px_80px_-20px_rgba(251,191,36,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Preview area, displays the export card scaled to fit */}
        <div className="p-5 border-b border-zinc-800/70">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
            Social card preview
          </div>
          <div className="rounded-xl overflow-hidden ring-1 ring-zinc-800/70 bg-zinc-950">
            {/* Fixed-size card used both for preview + PNG export */}
            <div
              ref={cardRef}
              data-testid="milestone-share-card"
              style={{
                width: "1200px",
                height: "630px",
                transform: "scale(0.55)",
                transformOrigin: "top left",
                position: "relative",
              }}
              className="relative overflow-hidden bg-zinc-950 text-zinc-50"
            >
              {/* Soft gold radial glow background */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(600px 400px at 82% 22%, rgba(251,191,36,0.22), transparent 60%), radial-gradient(700px 500px at 15% 90%, rgba(16,185,129,0.14), transparent 55%), linear-gradient(180deg, #0a0a0a 0%, #050505 100%)",
                }}
              />
              {/* Subtle grid pattern */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                  maskImage: "radial-gradient(circle at center, black, transparent 75%)",
                }}
              />

              {/* Content */}
              <div
                style={{
                  position: "relative",
                  padding: "56px 64px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  {/* Simple gold "OR" mark */}
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "16px",
                      background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      color: "#0a0a0a",
                      fontSize: "22px",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    OR
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        letterSpacing: "0.32em",
                        color: "#fcd34d",
                        fontWeight: 600,
                        textTransform: "uppercase",
                      }}
                    >
                      Intuiterview
                    </div>
                    <div style={{ fontSize: "13px", color: "#a1a1aa", marginTop: "2px" }}>
                      {milestone.label || "Career Milestone"}
                    </div>
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "72px",
                      lineHeight: 1.05,
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      color: "#fde68a",
                      maxWidth: "900px",
                    }}
                  >
                    {share.title || milestone.headline}
                  </div>
                  {milestone.message && (
                    <div
                      style={{
                        marginTop: "20px",
                        fontSize: "22px",
                        color: "#d4d4d8",
                        maxWidth: "820px",
                        lineHeight: 1.4,
                      }}
                    >
                      {milestone.message}
                    </div>
                  )}
                </div>

                <div>
                  {scoreCards.length > 0 && (
                    <div style={{ display: "flex", gap: "20px", marginBottom: "28px" }}>
                      {scoreCards.slice(0, 3).map((c, i) => {
                        const denom = c.denom;
                        const isSingle = c.single || typeof c.before !== "number";
                        return (
                          <div
                            key={i}
                            style={{
                              padding: "18px 22px",
                              borderRadius: "14px",
                              border: "1px solid rgba(251,191,36,0.22)",
                              background: "rgba(24,24,27,0.65)",
                              backdropFilter: "blur(4px)",
                              minWidth: "220px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                letterSpacing: "0.28em",
                                color: "#a1a1aa",
                                textTransform: "uppercase",
                              }}
                            >
                              {c.label}
                            </div>
                            {isSingle ? (
                              <div style={{ marginTop: "6px", display: "flex", alignItems: "baseline", gap: "6px" }}>
                                <span style={{ fontSize: "44px", fontWeight: 700, color: "#fde68a" }}>{c.after}</span>
                                <span style={{ fontSize: "18px", color: "#71717a" }}>
                                  {denom ? `/${denom}` : "%"}
                                </span>
                              </div>
                            ) : (
                              <div style={{ marginTop: "6px", display: "flex", alignItems: "baseline", gap: "10px" }}>
                                <span style={{ fontSize: "20px", color: "#71717a", textDecoration: "line-through" }}>
                                  {c.before}%
                                </span>
                                <span style={{ fontSize: "20px", color: "#52525b" }}>&rarr;</span>
                                <span style={{ fontSize: "36px", fontWeight: 700, color: "#fde68a" }}>{c.after}%</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "14px",
                      color: "#71717a",
                    }}
                  >
                    <div>Prepared with Intuiterview</div>
                    {hasBrand && <div style={{ color: "#a1a1aa" }}>intuiterview.com</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-zinc-800/70 space-y-3">
          {linkedInHint && (
            <div
              data-testid="linkedin-share-hint"
              className="rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3.5 py-2.5 text-[12px] leading-relaxed text-amber-100/90"
            >
              <span className="font-semibold text-amber-200">LinkedIn opened in a new tab.</span>{" "}
              Your caption is copied. Paste it into the post, then attach the PNG you downloaded.
              {!pngReady && (
                <span className="block mt-1 text-amber-200/80">
                  Tip: Tap <span className="font-semibold">Download PNG</span> first so it&apos;s ready to upload.
                </span>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
              data-testid="milestone-share-close"
            >
              Close
            </button>
            <div className="flex items-center gap-2 flex-wrap">
              {downloadErr && (
                <span className="text-[11px] text-rose-300">{downloadErr}</span>
              )}
              <button
                type="button"
                onClick={copy}
                data-testid="milestone-share-copy"
                className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900/60 text-zinc-200 hover:bg-zinc-800 px-3 py-2 text-sm font-medium transition-colors"
              >
                {copied ? <><Check className="h-4 w-4" />Copied</> : <><Copy className="h-4 w-4" />Copy Caption</>}
              </button>
              <button
                type="button"
                onClick={downloadPng}
                disabled={downloading}
                data-testid="milestone-share-download"
                className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900/60 text-zinc-200 hover:bg-zinc-800 disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 text-sm font-medium transition-colors"
              >
                {downloading ? <>Generating&hellip;</> : <><Download className="h-4 w-4" />{pngReady ? "PNG Saved" : "Download PNG"}</>}
              </button>
              <button
                type="button"
                onClick={shareToLinkedIn}
                data-testid="milestone-share-linkedin"
                className="inline-flex items-center gap-2 rounded-lg border border-[#0A66C2]/50 bg-[#0A66C2] text-white hover:bg-[#095199] px-3.5 py-2 text-sm font-semibold transition-colors shadow-[0_6px_20px_-8px_rgba(10,102,194,0.65)]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.89 1.63-1.83 3.36-1.83 3.6 0 4.27 2.37 4.27 5.45v6.27ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.12 20.45H3.55V9h3.57v11.45ZM22.22 0H1.78C.8 0 0 .78 0 1.75v20.5C0 23.22.8 24 1.78 24h20.44c.98 0 1.78-.78 1.78-1.75V1.75C24 .78 23.2 0 22.22 0Z" />
                </svg>
                Share to LinkedIn
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CareerMilestoneBanner({ milestone, onCTA }) {
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const seenRef = useRef(false);

  const key = milestone?.key;

  useEffect(() => {
    if (!milestone || !key) return undefined;
    if (hasSeenMilestone(key)) {
      // Show it briefly for continuity on the same page, but don't overwhelm
      // - auto-minimise faster.
      setVisible(true);
      const t = setTimeout(() => setMinimized(true), 6000);
      return () => clearTimeout(t);
    }
    seenRef.current = true;
    setVisible(true);
    // Persist that we've shown it so refreshes don't celebrate again.
    markMilestoneSeen(key);
    // Give the user a full minute to read + share before auto-minimising.
    // Faster celebrations felt rushed and hid the Share Achievement CTA.
    const t = setTimeout(() => setMinimized(true), 20000);
    return () => clearTimeout(t);
  }, [key, milestone]);

  if (!milestone || dismissed) return null;
  const accent = milestone.accent || "gold";
  const styles = ACCENT_STYLES[accent] || ACCENT_STYLES.gold;

  return (
    <>
      <ShareModal milestone={milestone} open={shareOpen} onClose={() => setShareOpen(false)} />

      <AnimatePresence>
        {visible && !minimized && (
          <motion.div
            key="banner"
            initial={{ opacity: 0, y: -18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            data-testid="career-milestone-banner"
            className={`relative overflow-hidden rounded-2xl border ${styles.ring} bg-gradient-to-br ${styles.surface} ${styles.glow} px-6 py-6 sm:px-8 sm:py-7`}
          >
            {milestone.confetti && <Confetti />}

            {/* Close button */}
            <button
              type="button"
              onClick={() => setMinimized(true)}
              aria-label="Minimise milestone"
              data-testid="milestone-minimise-btn"
              className="absolute top-3 right-3 h-7 w-7 rounded-full border border-zinc-700/60 bg-zinc-950/40 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500 flex items-center justify-center transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>

            <div className="relative flex flex-wrap items-start gap-5">
              {/* Left icon column */}
              <div className="flex flex-col items-center gap-1.5">
                <div className={`h-11 w-11 rounded-xl border ${styles.ring} bg-zinc-950/60 flex items-center justify-center`}>
                  <Trophy className={`h-5 w-5 ${styles.headline}`} />
                </div>
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${styles.dot} animate-pulse`} />
              </div>

              <div className="flex-1 min-w-0">
                {/* Label chip */}
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    data-testid="milestone-label"
                    className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] font-semibold border rounded-full px-2.5 py-0.5 ${styles.label}`}
                  >
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${styles.dot}`} />
                    {milestone.label || "Career Milestone"}
                  </span>
                </div>

                <motion.h2
                  data-testid="milestone-headline"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.5 }}
                  className={`mt-3 text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1] ${styles.headline}`}
                >
                  {milestone.headline}
                </motion.h2>
                {milestone.message && (
                  <p
                    data-testid="milestone-message"
                    className="mt-2 text-sm sm:text-base text-zinc-300 max-w-2xl leading-relaxed"
                  >
                    {milestone.message}
                  </p>
                )}

                {milestone.scoreCards?.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-3">
                    {milestone.scoreCards.map((card, i) => (
                      <ScoreCard key={i} card={card} accentTone={accent} />
                    ))}
                  </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {milestone.cta && (
                    <button
                      type="button"
                      data-testid="milestone-cta-btn"
                      onClick={() => {
                        if (onCTA) onCTA(milestone);
                        if (milestone.cta?.scrollToTestId) {
                          const el = document.querySelector(`[data-testid='${milestone.cta.scrollToTestId}']`);
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }}
                      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${styles.ctaBtn}`}
                    >
                      {milestone.cta.label}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="milestone-share-btn"
                    onClick={() => setShareOpen(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-950/40 text-zinc-200 hover:bg-zinc-900 px-3.5 py-2 text-sm font-medium transition-colors"
                  >
                    <Share2 className="h-4 w-4" />
                    Share Achievement
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {visible && minimized && !dismissed && (
          <motion.button
            key="pill"
            type="button"
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            onClick={() => setMinimized(false)}
            data-testid="milestone-pill"
            className={`fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full border ${styles.ring} bg-zinc-950/90 backdrop-blur px-4 py-2 text-xs font-semibold text-zinc-100 shadow-lg hover:shadow-xl hover:border-amber-400/60 transition-shadow`}
          >
            <Sparkles className={`h-3.5 w-3.5 ${styles.headline}`} />
            <span className={styles.headline}>{milestone.headline}</span>
            <ChevronUp className="h-3.5 w-3.5 text-zinc-400" />
            <span
              onClick={(e) => {
                e.stopPropagation();
                setDismissed(true);
              }}
              className="ml-1 -mr-1 h-5 w-5 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800/70 cursor-pointer"
              role="button"
              aria-label="Dismiss milestone"
              data-testid="milestone-dismiss-btn"
            >
              <X className="h-3 w-3" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
